import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BuyerDetailsEditor } from "@/components/buyer/BuyerDetailsEditor";
import { BTN_BLACK, BTN_WHITE, INPUT, errText } from "@/components/pageSettings/AdminModal";
import { CopyFromBuyer } from "./CopyFromBuyer";
import { Field, StepShell, setupUrl } from "./StepShell";

const PRIVATE = [["contact_name", "Contact person"], ["contact_email", "Contact email * (their login)"], ["contact_phone", "Contact phone"]];
const SHOWN = [
  ["firm_name", "Firm name *"], ["phone", "Company phone"], ["company_email", "Company email (optional)"],
  ["legal_name", "Legal name for disclaimers"], ["office_location", "Office address"],
];
const EMAILS = ["contact_email", "company_email"];

function Group({ title, hint, rows, f, setF, testId }) {
  return (
    <div data-testid={testId}>
      <div className="label-mono">{title}</div>
      <p className="text-[11px] text-zinc-500 mb-2">{hint}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {rows.map(([k, label]) => (
          <Field key={k} label={label}>
            <input className={INPUT} type={EMAILS.includes(k) ? "email" : "text"} value={f[k]}
                   onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} data-testid={`setup-company-${k.replace(/_/g, "-")}`} />
          </Field>
        ))}
      </div>
    </div>
  );
}

function NewCompany({ draft, onCreated }) {
  const [f, setF] = useState(() => ({
    firm_name: draft?.firm_name || "", contact_email: "", contact_name: "", contact_phone: "", phone: "",
    company_email: "", legal_name: draft?.legal_name || "", office_location: "",
  }));
  const [copyFrom, setCopyFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.firm_name.trim().length < 2) { toast.error("Enter the firm name"); return; }
    if (!/^\S+@\S+\.\S+$/.test(f.contact_email.trim())) { toast.error("Enter a valid contact email"); return; }
    if (f.company_email.trim() && !/^\S+@\S+\.\S+$/.test(f.company_email.trim())) { toast.error("Enter a valid company email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/buyer-setup", {
        ...f, company_email: f.company_email.trim() || null, draft_profile_id: draft?.id || null, copy_from_org_id: copyFrom || null,
      });
      toast.success(data.copied ? `${data.org.name} saved with ${data.copied.from}'s setup. Check each step. Nothing is live yet.`
        : `${data.org.name} saved. Nothing is live yet.`);
      onCreated(data);
    } catch (e) {
      toast.error(errText(e, "Couldn't save the company"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <StepShell title="Company & contact" testId="setup-step-company"
               hint="Who they are. Nothing goes live until a test lead reaches their system and you press Go live."
               footer={<button type="button" className={BTN_BLACK} onClick={save} disabled={busy} data-testid="setup-company-create">{busy ? "Saving…" : "Save & continue"}</button>}>
      {!draft && <CopyFromBuyer value={copyFrom} onChange={setCopyFrom} />}
      <Group title="Company details shown on their pages" hint="Visitors see these on landing and thank-you pages."
             rows={SHOWN} f={f} setF={setF} testId="setup-company-shown" />
      <Group title="Contact person (private)" hint="Used for their login and our emails. Never shown on any page."
             rows={PRIVATE} f={f} setF={setF} testId="setup-company-private" />
    </StepShell>
  );
}

function ExistingCompany({ state, orgId, onSaved }) {
  const [busy, setBusy] = useState(false);
  const reload = () => api.get(setupUrl(orgId)).then((r) => onSaved(r.data));
  const addPage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${setupUrl(orgId)}/profile`);
      toast.success("Buyer page created as a draft");
      onSaved(data);
    } catch (e) {
      toast.error(errText(e, "Couldn't create their page"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <StepShell title="Company & contact" testId="setup-step-company"
               hint="Saving here also updates their pages and thank-you pages."
               footer={<button type="button" className={BTN_BLACK} onClick={() => onSaved(state, "criteria")} data-testid="setup-company-next">Next: lead criteria</button>}>
      {!state.profile && (
        <div className="border-2 border-amber-500 bg-amber-50 p-3 text-xs flex flex-wrap items-center gap-2" data-testid="setup-company-no-page">
          <span className="flex-1 min-w-[200px]">They don't have a buyer page yet. Every landing page sends leads through it.</span>
          <button type="button" className={BTN_WHITE} onClick={addPage} disabled={busy} data-testid="setup-company-add-page">Create their page</button>
        </div>
      )}
      <BuyerDetailsEditor key={state.profile?.id || "none"} orgId={orgId} onSaved={reload} />
    </StepShell>
  );
}

export function CompanyStep({ state, orgId, onSaved, onCreated, carry }) {
  if (!orgId) return <NewCompany draft={carry.draft} onCreated={onCreated} />;
  return <ExistingCompany state={state} orgId={orgId} onSaved={onSaved} />;
}
